import { useTranslation } from 'react-i18next';
import { Tag, Plus, Pencil, Wrench, Trash2 } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { APP_VERSION, changelog, type ChangelogEntry } from '@/lib/changelog';

// Type-pill palette. Each tone has a paired light/dark variant. The
// dark variants use a 15% alpha tinted background so the pill reads
// as colourful-but-subtle on slate-800 cards rather than the harsh
// flat tones that the bg-XXX-100/text-XXX-700 light combo turns into
// after a naive dark-mode swap.
const typeConfig = {
  added: {
    icon: Plus,
    label: 'Adăugat',
    color:
      'border border-emerald-200 bg-emerald-50 text-emerald-700 ' +
      'dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  changed: {
    icon: Pencil,
    label: 'Modificat',
    color:
      'border border-sky-200 bg-sky-50 text-sky-700 ' +
      'dark:border-sky-500/30 dark:bg-sky-500/15 dark:text-sky-300',
  },
  fixed: {
    icon: Wrench,
    label: 'Reparat',
    color:
      'border border-amber-200 bg-amber-50 text-amber-700 ' +
      'dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300',
  },
  removed: {
    icon: Trash2,
    label: 'Eliminat',
    color:
      'border border-red-200 bg-red-50 text-red-700 ' +
      'dark:border-red-500/30 dark:bg-red-500/15 dark:text-red-300',
  },
} as const;

export function ChangelogPage() {
  const { t } = useTranslation(['common']);

  return (
    <div className="animate-fade-in mx-auto max-w-3xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
          <Tag className="h-5 w-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {t('changelog.title', { defaultValue: 'Changelog' })}
            </h1>
            <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
              v{APP_VERSION}
            </span>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('changelog.subtitle', {
              defaultValue: 'Istoricul modificărilor Pnerp',
            })}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {changelog.map((entry, i) => (
          <VersionCard key={entry.version} entry={entry} isLatest={i === 0} />
        ))}
      </div>
    </div>
  );
}

function VersionCard({ entry, isLatest }: { entry: ChangelogEntry; isLatest: boolean }) {
  const grouped = {
    added: entry.changes.filter((c) => c.type === 'added'),
    changed: entry.changes.filter((c) => c.type === 'changed'),
    fixed: entry.changes.filter((c) => c.type === 'fixed'),
    removed: entry.changes.filter((c) => c.type === 'removed'),
  };

  return (
    <Card>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-lg font-bold text-slate-900 dark:text-slate-100">
            v{entry.version}
          </span>
          {isLatest && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
              Curent
            </span>
          )}
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {entry.date}
          </span>
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
            — {entry.title}
          </span>
        </div>

        {(Object.keys(grouped) as Array<keyof typeof grouped>).map((type) => {
          const items = grouped[type];
          if (items.length === 0) return null;
          const cfg = typeConfig[type];
          const Icon = cfg.icon;
          return (
            <div key={type}>
              <div
                className={`mb-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${cfg.color}`}
              >
                <Icon className="h-3 w-3" />
                {cfg.label}
              </div>
              <ul className="space-y-1.5 pl-1">
                {items.map((item) => (
                  <li
                    key={item.text}
                    className="flex items-start gap-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300 dark:bg-slate-500" />
                    <span dangerouslySetInnerHTML={{ __html: renderInline(item.text) }} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}

/**
 * Light-touch markdown for the changelog: `**bold**` → <strong>, and
 * `` `code` `` → <code>. Anything else passes through. Source text is
 * authored in `apps/web/src/lib/changelog.ts` so we trust it — no XSS
 * sanitisation is needed.
 */
function renderInline(text: string): string {
  // Escape HTML chars first so user-authored "<" doesn't break the markup.
  const esc = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return esc
    .replace(
      /\*\*([^*]+)\*\*/g,
      '<strong class="font-semibold text-slate-900 dark:text-slate-50">$1</strong>',
    )
    .replace(
      /`([^`]+)`/g,
      '<code class="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-800 dark:bg-slate-800 dark:text-slate-200">$1</code>',
    );
}
